import React, { createContext, useEffect, useState } from 'react';

import { theDarkTheme, theLightTheme } from '../theme/theme';
import { withViewTransition } from '../utils/viewTransition';

export const ThemeContext = createContext()

const STORAGE_KEY = 'theme'

// A choice the visitor made earlier wins; otherwise follow their system
// setting, and fall back to dark. Storage can be unavailable (private mode,
// blocked cookies), so every access is guarded. (index.html applies the same
// rule before first paint to avoid a flash.)
const readInitialTheme = () => {
    try {
        const saved = window.localStorage.getItem(STORAGE_KEY)
        if (saved === 'light' || saved === 'dark') return saved
    } catch (e) { /* ignore */ }
    if (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: light)').matches) {
        return 'light'
    }
    return 'dark'
}

// Only an explicit toggle is remembered, so people who never touch it keep following their system.
const remember = (isDarkNext) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, isDarkNext ? 'dark' : 'light')
    } catch (e) { /* storage unavailable: the choice just won't persist */ }
}

function ThemeContextProvider(props) {
    const [isDark, setDark] = useState(() => readInitialTheme() === 'dark');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const theme = isDark ? theDarkTheme : theLightTheme;

    useEffect(() => {
        const mode = isDark ? 'dark' : 'light'
        document.documentElement.setAttribute('data-theme', mode)
        const meta = document.querySelector('meta[name="theme-color"]')
        if (meta) meta.setAttribute('content', isDark ? '#0d1013' : '#f5f2ec')
    }, [isDark])

    const setHandleDrawer = () => {
        setDrawerOpen(!drawerOpen)
    }

    // Switch themes. Where the View Transitions API exists (and motion is
    // allowed) the new theme is revealed as a circle growing from the click
    // point; everywhere else it simply swaps.
    const changeTheme = (event) => {
        const next = !isDark
        remember(next)
        const origin = {
            x: event && event.clientX ? event.clientX : window.innerWidth / 2,
            y: event && event.clientY ? event.clientY : 0,
        }
        withViewTransition(() => {
            document.documentElement.setAttribute('data-theme', next ? 'dark' : 'light')
            setDark(next)
        }, 'theme', origin)
    }

    const value = { theme, drawerOpen, setHandleDrawer, changeTheme, isDark }
    return (
        <ThemeContext.Provider value={value}>
            {props.children}
        </ThemeContext.Provider>
    )
}


export default ThemeContextProvider
